<script lang="ts">
	import { getDeviceSettings } from 'src/stores/device-runtime/status-selectors';
	//Services
	import { format } from '../../../../services/i18n';
	import { gen2Request } from '../../../../services/gen_2_http_reqs';
	//Helpers
	import { real_id } from '../../../../helpers/interface_helpers';
	//Stores
		import { showNewLayout } from 'src/stores/toDelete/new_layout';
	//Types
	import type { SettingRequest } from '../../../../types/requests/settings/setting';
	import type { Writable } from 'svelte/store';
	//Design system components
	import { Alert, Button, Checkbox, Input, Radio, Text } from '@Shelly/shelly-design-system';
	import { onSettingsResponse } from 'src/helpers/device_settings_helpers';
	import { getErrorOrInfoMessageForMinAndMax } from 'src/helpers/utilities';
	import { parseID } from './ThermostatsWallDisplay/helpers';
	import { getContext, onDestroy } from 'svelte';
	import type { SidepanelStickyBottomProps } from 'src/components/Sidepanel/sidepanel-types';

	$: settingsStore = getDeviceSettings(id);
	export let device;

	$: isSensor =
		typeof settings?.['thermostat:0'] !== 'undefined' &&
		parseID(settings?.['thermostat:0']?.sensor) === device.id; //this i

	let id = real_id(device.id),
		settings = $settingsStore,
		timeout = $settingsStore?.ui?.screen_saver?.timeout,
		cacheTimeout = timeout,
		enable = $settingsStore?.ui?.screen_saver?.enable,
		cacheEnabled = enable,
		priority_element = $settingsStore?.ui?.screen_saver?.priority_element,
		cachePriority = priority_element,
		loading = false;

	const min = 10,
		max = 3600;

	const options = ['CLOCK', 'TEMPERATURE', 'HUMIDITY'];
	const validate = (value) => {
		if (value > max) {
			value = max;
		}
		if (value < min) {
			value = min;
		}
		return +value;
	};

	const sendRequest = async () => {
		if (inputData.error) return;
		if (cacheEnabled === enable && cachePriority === priority_element && cacheTimeout === timeout) {
			return;
		}
		loading = true;
		timeout = validate(timeout);

		const params = {
			id: device.channel,
			config: {
				screen_saver: {
					enable,
					timeout,
					priority_element,
				},
			},
		};

		const res: SettingRequest = await gen2Request(device, `ui.SetConfig`, params);
		const response = await onSettingsResponse(res, device);
		if (response) {
			settings.ui.screen_saver = {
				enable,
				timeout,
				priority_element,
			};
			devices_settings.update_devices_settings(id, settings);

			cacheEnabled = enable;
			cachePriority = priority_element;
			cacheTimeout = timeout;
		} else {
			enable = cacheEnabled;
			priority_element = cachePriority;
			timeout = cacheTimeout;
		}
		loading = false;
	};

	$: inputData = getErrorOrInfoMessageForMinAndMax(timeout, min, max);

	const stickyBottomAction =
		getContext<Writable<SidepanelStickyBottomProps | null>>('stickyBottomAction');

	$: if (stickyBottomAction) {
		if ($showNewLayout) {
			stickyBottomAction.set({
				onClick: sendRequest,
				loading,
				disabled: loading || !!inputData.error,
			});
		} else {
			stickyBottomAction.set(null);
		}
	}

	onDestroy(() => {
		stickyBottomAction?.set(null);
	});
</script>

<Checkbox
	checked={enable}
	size="m"
	value={enable}
	on:click={() => (enable = !enable)}
	id="checkbox-je9uyzv">{$format('enable_screen_saver', { default: 'Enable screensaver.' })}</Checkbox
>
<Text styleType="body-small" class="mb-s">
	{$format('screen_saver_timeout_description', {
		values: { value: timeout, min: min, max: max },
		default: 'Set screensaver timeout. Values must be from {min} to {max} in seconds.',
	})}
</Text>
{#if isSensor}
	<Alert
		class="mb-s"
		heading={$format('label_info')}
		message={$format('alert_screen_timeout_disable_description', {
			default:
				'Screen timeout setting is disabled while your device is used for thermostat sensor.',
		})}
	/>
{/if}
<Input
	class="mb-m"
	size="m"
	bind:value={timeout}
	type={'number'}
	{min}
	{max}
	disabled={isSensor}
	error={inputData.error}
	info={inputData.info}
	step={1}
	on:change={() => {
		timeout = validate(timeout);
	}}
	on:enter={sendRequest}
	id="input-h88dq5m"
	aria-label={$format('screen_saver_timeout_description', {
		default: 'Set screensaver timeout',
	})}
/>

{#each options as option}
	<Radio
		selected={priority_element}
		size="m"
		value={option}
		on:click={() => (priority_element = option)}
		id="radio-e76tjdz"
		>{$format(`show_${option.toLowerCase()}`, {
			default: 'Show' + option,
		})}</Radio
	>
{/each}

{#if !$showNewLayout}
	<Button class="mb-s" {loading} size="m" width="full" on:click={sendRequest}
		>{$format('button_save')}</Button
	>
{/if}
